import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import SitesWebSelect from '../iti/SitesWebSelect';
import SitesWebStickyHead from '../iti/SitesWebStickyHead';
import SiteClientInlineCell from '../iti/SiteClientInlineCell';
import { AppAlert, AppButton } from '../ui';
import {
  getDaysRemainingText,
  getRenewalStateText,
  getStatusText,
} from '../../utils/domainUtils';
import '../../styles/sites-web.scss';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR');
};

const statusClass = (status) => {
  if (status === 'active') return 'sw-status--on';
  if (status === 'expiring') return 'sw-status--soon';
  if (status === 'expired') return 'sw-status--off';
  return 'sw-status--unknown';
};

const renewalClass = (state) => {
  if (state === 'automatic') return 'sw-status--on';
  if (state === 'manual') return 'sw-status--soon';
  if (state === 'no_renew') return 'sw-status--off';
  return 'sw-status--unknown';
};

function formatInvoiceDate(value) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR');
}

function collectYearOptions(domains, field) {
  const years = new Set();
  domains.forEach((domain) => {
    const date = domain[field]?.date;
    if (typeof date === 'string' && date.length >= 4) years.add(date.slice(0, 4));
  });
  return [...years].sort((a, b) => b.localeCompare(a)).map((year) => ({ value: year, label: year }));
}

function DomainDocCell({ doc, emptyLabel }) {
  if (!doc) return <span className="sw-none">Aucune</span>;
  const dateLabel = formatInvoiceDate(doc.date);
  const done = doc.status === 'paid' || doc.status === 'closed';
  const label = doc.status === 'paid'
    ? 'Payée'
    : doc.status === 'unpaid'
      ? 'À régler'
      : doc.status === 'validated'
        ? 'Validée'
        : doc.status === 'progress'
          ? 'En cours'
          : doc.status === 'closed'
            ? 'Clôturée'
            : doc.statusLabel;
  return (
    <a
      href={doc.url || undefined}
      target={doc.url ? '_blank' : undefined}
      rel={doc.url ? 'noopener noreferrer' : undefined}
      className="sw-invoice"
      title={doc.productLabel || undefined}
      onClick={(event) => {
        if (!doc.url) event.preventDefault();
      }}
    >
      <span className="sw-invoice__ref">{doc.ref || dateLabel || emptyLabel}</span>
      <span className="sw-invoice__meta">
        {doc.ref && dateLabel ? dateLabel : null}
        {label ? <span className={`sw-pay ${done ? 'sw-pay--paid' : 'sw-pay--due'}`}>{label}</span> : null}
      </span>
    </a>
  );
}

const DomainListPage = ({ adapter }) => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [apiWorking, setApiWorking] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusTab, setStatusTab] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    daysRemaining: [],
    renewalState: [],
    clientLink: [],
    domainInvoice: [],
    domainInvoiceYear: [],
    domainOrder: [],
    domainOrderYear: [],
  });
  const itemsPerPage = 20;

  const fetchAndSetDomains = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!adapter.isConfigured()) {
      setError('Service API non configuré. Vérifiez REACT_APP_API_URL.');
      setLoading(false);
      return;
    }
    try {
      const domainsData = await adapter.listDomains();
      setDomains(domainsData);
      setApiWorking(true);
    } catch (err) {
      setApiWorking(false);
      setDomains(adapter.getFallbackDomains());
      setError(err.message || 'Erreur lors du chargement des domaines');
    } finally {
      setLoading(false);
    }
  }, [adapter]);

  useEffect(() => {
    fetchAndSetDomains();
  }, [fetchAndSetDomains]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusTab, filters]);

  const counts = useMemo(() => {
    const next = { all: domains.length, active: 0, expiring: 0, expired: 0 };
    domains.forEach((domain) => {
      const status = adapter.getStatus(domain);
      if (next[status] != null) next[status] += 1;
    });
    return next;
  }, [adapter, domains]);

  const filteredDomains = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase();
    return domains.filter((domain) => {
      const name = (adapter.getName(domain) || '').toLowerCase();
      const meta = (adapter.getMeta?.(domain) || '').toLowerCase();
      if (term && !name.includes(term) && !meta.includes(term)) return false;

      const status = adapter.getStatus(domain);
      if (statusTab !== 'all' && status !== statusTab) return false;

      if (filters.daysRemaining.length > 0) {
        const days = adapter.getDays(domain);
        const bucket = filters.daysRemaining[0];
        const match = bucket === 'expired'
          ? days != null && days < 0
          : bucket === 'critical'
            ? days != null && days >= 0 && days < 7
            : bucket === 'warning'
              ? days != null && days >= 7 && days < 30
              : bucket === 'safe'
                ? days != null && days >= 30
                : false;
        if (!match) return false;
      }

      if (filters.renewalState.length > 0) {
        if (adapter.getRenewal(domain) !== filters.renewalState[0]) return false;
      }

      if ((filters.clientLink || []).length > 0) {
        const linked = Number(domain.dolibarrThirdpartyId) > 0;
        if (filters.clientLink[0] === 'linked' && !linked) return false;
        if (filters.clientLink[0] === 'unlinked' && linked) return false;
      }

      if ((filters.domainInvoice || []).length > 0) {
        const key = domain.domainInvoice?.status || 'none';
        if (key !== filters.domainInvoice[0]) return false;
      }

      if ((filters.domainInvoiceYear || []).length > 0) {
        const year = typeof domain.domainInvoice?.date === 'string' ? domain.domainInvoice.date.slice(0, 4) : '';
        if (year !== filters.domainInvoiceYear[0]) return false;
      }

      if ((filters.domainOrder || []).length > 0) {
        const key = domain.domainOrder?.status || 'none';
        if (key !== filters.domainOrder[0]) return false;
      }

      if ((filters.domainOrderYear || []).length > 0) {
        const year = typeof domain.domainOrder?.date === 'string' ? domain.domainOrder.date.slice(0, 4) : '';
        if (year !== filters.domainOrderYear[0]) return false;
      }

      return true;
    });
  }, [adapter, debouncedSearchTerm, domains, filters, statusTab]);

  const sortedDomains = useMemo(
    () => [...filteredDomains].sort((a, b) => (adapter.getName(a) || '').localeCompare(adapter.getName(b) || '', 'fr')),
    [adapter, filteredDomains],
  );

  const pageCount = Math.max(Math.ceil(sortedDomains.length / itemsPerPage), 1);
  const safePage = Math.min(currentPage, pageCount);
  const startIndex = (safePage - 1) * itemsPerPage;
  const currentDomains = sortedDomains.slice(startIndex, startIndex + itemsPerPage);
  const rangeStart = sortedDomains.length === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + itemsPerPage, sortedDomains.length);

  const invoiceYearOptions = useMemo(() => collectYearOptions(domains, 'domainInvoice'), [domains]);
  const orderYearOptions = useMemo(() => collectYearOptions(domains, 'domainOrder'), [domains]);

  const activeFilterCount = ['daysRemaining', 'renewalState', 'clientLink', 'domainInvoice', 'domainInvoiceYear', 'domainOrder', 'domainOrderYear']
    .filter((key) => (filters[key] || []).length > 0).length;
  const selected = (key) => (filters[key] && filters[key][0] ? String(filters[key][0]) : '');
  const setSelected = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value ? [value] : [] }));
  };

  const resetFilters = () => {
    setFilters({
      daysRemaining: [],
      renewalState: [],
      clientLink: [],
      domainInvoice: [],
      domainInvoiceYear: [],
      domainOrder: [],
      domainOrderYear: [],
    });
    setStatusTab('all');
    setSearchTerm('');
  };

  const linkClients = async () => {
    setLinking(true);
    setSyncMessage(null);
    setError(null);
    try {
      const result = await adapter.autoLinkClients();
      const fromSite = result?.data?.linked_from_site ?? 0;
      const fromAnnuaire = result?.data?.linked_from_annuaire ?? 0;
      setSyncMessage(`Correspondance terminée : ${fromSite} repris depuis les sites, ${fromAnnuaire} depuis l’annuaire.`);
      await fetchAndSetDomains();
    } catch (err) {
      setError(err.message || 'Erreur lors de la correspondance client');
    } finally {
      setLinking(false);
    }
  };

  const syncFromRegistrar = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setError(null);
    try {
      const result = await adapter.syncDomains();
      const synced = result?.data?.synced;
      setSyncMessage(synced != null ? `Synchronisation terminée : ${synced} domaine(s) mis à jour.` : (result?.message || 'Synchronisation terminée.'));
      await fetchAndSetDomains();
    } catch (err) {
      setError(err.message || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const renderDomain = (domain) => {
    const name = adapter.getName(domain) || '—';
    const meta = adapter.getMeta?.(domain) || '';
    const status = adapter.getStatus(domain);
    const renewal = adapter.getRenewal(domain);
    const days = adapter.getDays(domain);
    const letter = name.trim().charAt(0).toUpperCase() || '?';
    const href = name.includes('.') ? `https://${name.replace(/\/$/, '')}` : null;

    return {
      name: (
        <div className="sw-site">
          <span className="sw-letter">{letter}</span>
          <span className="sw-site__text">
            {href ? (
              <a className="sw-site__name" href={href} target="_blank" rel="noopener noreferrer">{name}</a>
            ) : (
              <span className="sw-site__name">{name}</span>
            )}
            {meta ? <span className="sw-site__id">{meta}</span> : null}
          </span>
        </div>
      ),
      status: (
        <span className={`sw-status ${statusClass(status)}`}>
          <span className="sw-status__dot" />
          {getStatusText(status)}
        </span>
      ),
      expiration: <span className="sw-server">{formatDate(adapter.getExpiration(domain))}</span>,
      days: <span className="sw-server">{getDaysRemainingText(days)}</span>,
      renewal: (
        <span className={`sw-status ${renewalClass(renewal)}`}>
          <span className="sw-status__dot" />
          {getRenewalStateText(renewal)}
        </span>
      ),
      client: (
        <SiteClientInlineCell
          siteInfo={domain}
          apiWorking={apiWorking}
          layout="sites"
          onLink={adapter.linkClient}
          onUpdated={(domainId, updated) => {
            setDomains((list) => list.map((item) => (item.id === domainId ? { ...item, ...updated } : item)));
          }}
        />
      ),
      invoice: <DomainDocCell doc={domain.domainInvoice} emptyLabel="Facture" />,
      order: <DomainDocCell doc={domain.domainOrder} emptyLabel="Commande" />,
      gear: (
        <AppButton
          className="sw-gear"
          icon="lucide:settings"
          iconWidth={15}
          onClick={() => navigate(adapter.detailPath(domain.id))}
          title="Gérer le domaine"
          aria-label="Gérer le domaine"
        />
      ),
    };
  };

  return (
    <MasterLayout>
      <div className="sites-web">
        <div className="sw-head">
          <div className="sw-head__titles">
            <h1 className="fw-semibold mb-0 app-page-title">{adapter.title}</h1>
            <p>{adapter.subtitle}</p>
          </div>
          <div className="sw-head__actions">
            <AppButton className="sw-btn" icon="lucide:refresh-cw" iconWidth={16} onClick={fetchAndSetDomains} disabled={loading || syncing || linking} loading={loading} loadingLabel="Actualiser">
              Actualiser
            </AppButton>
            <AppButton className="sw-btn" icon="lucide:link" iconWidth={16} onClick={linkClients} disabled={linking || loading || syncing || !apiWorking} loading={linking} loadingLabel="Relier automatiquement">
              Relier automatiquement
            </AppButton>
            <AppButton className="sw-btn sw-btn--dark" icon="lucide:download-cloud" iconWidth={16} onClick={syncFromRegistrar} disabled={syncing || loading || linking || !apiWorking} loading={syncing} loadingLabel={adapter.syncLabel}>
              {adapter.syncLabel}
            </AppButton>
          </div>
        </div>

        {error && (
          <AppAlert className="sw-note">
            {error}
            <AppButton className="sw-btn" onClick={fetchAndSetDomains}>Réessayer</AppButton>
          </AppAlert>
        )}

        {!apiWorking && (
          <AppAlert variant="warning" className="sw-note">
            Mode test : l’API n’est pas accessible. Les chiffres affichés sont des exemples.
          </AppAlert>
        )}

        {syncMessage && (
          <AppAlert variant="info" className="sw-note" role="status">{syncMessage}</AppAlert>
        )}

        <div className="sw-stats">
          {[
            { key: 'all', label: 'Tous les domaines', hint: 'Enregistrés chez ce registrar', dot: 'var(--sw-placeholder)' },
            { key: 'active', label: 'Actifs', hint: 'Expiration dans plus de 30 jours', dot: 'var(--sw-ok-dot)' },
            { key: 'expiring', label: 'Expire bientôt', hint: 'Moins de 30 jours', dot: 'var(--sw-due-fg)' },
            { key: 'expired', label: 'Expirés', hint: 'Date d’expiration dépassée', dot: 'var(--sw-bad-dot)' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`sw-stat${statusTab === tab.key ? ' is-on' : ''}`}
              onClick={() => setStatusTab(tab.key)}
            >
              <span className="sw-stat__label"><span className="sw-stat__dot" style={{ background: tab.dot }} />{tab.label}</span>
              <span className="sw-stat__count">{counts[tab.key] || 0}</span>
              <span className="sw-stat__hint">{tab.hint}</span>
            </button>
          ))}
        </div>

        <div className="sw-panel">
          <div className="sw-search-row">
            <label className="sw-search">
              <Icon icon="lucide:search" width={17} />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Rechercher un domaine…"
              />
            </label>
            <AppButton className="sw-btn sw-filters-toggle" icon="lucide:sliders-horizontal" iconWidth={16} onClick={() => setFiltersOpen((open) => !open)}>
              {`Filtres${activeFilterCount ? ` (${activeFilterCount})` : ''}`}
            </AppButton>
            {(activeFilterCount > 0 || statusTab !== 'all' || searchTerm) && (
              <button type="button" className="sw-clear" onClick={resetFilters}>
                <Icon icon="lucide:filter-x" width={16} />
                Effacer les filtres
              </button>
            )}
          </div>
          <div className={`sw-filters${filtersOpen ? ' is-open' : ''}`}>
            <SitesWebSelect
              label="Jours restants"
              value={selected('daysRemaining')}
              onChange={(value) => setSelected('daysRemaining', value)}
              placeholder="Tous"
              options={[
                { value: 'expired', label: 'Expirés' },
                { value: 'critical', label: 'Critiques (< 7 j)' },
                { value: 'warning', label: 'Attention (7-29 j)' },
                { value: 'safe', label: 'Sécurisés (30 j et +)' },
              ]}
            />
            <SitesWebSelect
              label="Renouvellement"
              value={selected('renewalState')}
              onChange={(value) => setSelected('renewalState', value)}
              placeholder="Tous"
              options={[
                { value: 'automatic', label: 'Automatique' },
                { value: 'manual', label: 'Manuel' },
                { value: 'no_renew', label: 'Pas de renouvellement' },
              ]}
            />
            <SitesWebSelect
              label="Client"
              value={selected('clientLink')}
              onChange={(value) => setSelected('clientLink', value)}
              placeholder="Tous"
              options={[
                { value: 'linked', label: 'Avec client' },
                { value: 'unlinked', label: 'Sans client' },
              ]}
            />
            <SitesWebSelect
              label="Facture domaine"
              value={selected('domainInvoice')}
              onChange={(value) => setSelected('domainInvoice', value)}
              placeholder="Toutes"
              options={[
                { value: 'paid', label: 'Payée' },
                { value: 'unpaid', label: 'À régler' },
                { value: 'none', label: 'Aucune' },
              ]}
            />
            <SitesWebSelect
              label="Année de facture"
              value={selected('domainInvoiceYear')}
              onChange={(value) => setSelected('domainInvoiceYear', value)}
              placeholder="Toutes"
              searchable={invoiceYearOptions.length > 8}
              options={invoiceYearOptions}
            />
            <SitesWebSelect
              label="Commande domaine"
              value={selected('domainOrder')}
              onChange={(value) => setSelected('domainOrder', value)}
              placeholder="Toutes"
              options={[
                { value: 'validated', label: 'Validée' },
                { value: 'progress', label: 'En cours' },
                { value: 'closed', label: 'Clôturée' },
                { value: 'none', label: 'Aucune' },
              ]}
            />
            <SitesWebSelect
              label="Année de commande"
              value={selected('domainOrderYear')}
              onChange={(value) => setSelected('domainOrderYear', value)}
              placeholder="Toutes"
              searchable={orderYearOptions.length > 8}
              options={orderYearOptions}
            />
          </div>
        </div>

        <div className="sw-board">
          {loading ? (
            <div className="sw-empty">Chargement des domaines…</div>
          ) : currentDomains.length === 0 ? (
            <div className="sw-empty">Aucun domaine trouvé</div>
          ) : (
            <>
              <div className="sw-desktop">
                <SitesWebStickyHead className="sw-grid sw-grid--domains">
                  <span>Domaine</span>
                  <span>Client</span>
                  <span>Statut</span>
                  <span>Facture domaine</span>
                  <span>Commande domaine</span>
                  <span>Expiration</span>
                  <span>Jours restants</span>
                  <span>Renouvellement</span>
                  <span />
                </SitesWebStickyHead>
                {currentDomains.map((domain) => {
                  const parts = renderDomain(domain);
                  return (
                    <div className="sw-grid sw-grid--domains sw-row" key={domain.id}>
                      {parts.name}
                      {parts.client}
                      {parts.status}
                      {parts.invoice}
                      {parts.order}
                      {parts.expiration}
                      {parts.days}
                      {parts.renewal}
                      {parts.gear}
                    </div>
                  );
                })}
              </div>
              <div className="sw-cards">
                {currentDomains.map((domain) => {
                  const parts = renderDomain(domain);
                  return (
                    <div className="sw-card" key={domain.id}>
                      <div className="sw-card__top">
                        {parts.name}
                        {parts.gear}
                      </div>
                      {parts.status}
                      {parts.client}
                      <div className="sw-card__invoice">
                        Facture domaine
                        {parts.invoice}
                      </div>
                      <div className="sw-card__invoice">
                        Commande domaine
                        {parts.order}
                      </div>
                      <div className="sw-card__invoice">
                        Expiration {parts.expiration}
                        <span>·</span>
                        {parts.days}
                      </div>
                      {parts.renewal}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!loading && sortedDomains.length > 0 && (
            <div className="sw-pager">
              <span className="sw-pager__count">{rangeStart} à {rangeEnd} sur {sortedDomains.length} domaines</span>
              <div className="sw-pager__nav">
                <AppButton className="sw-btn sw-btn--page" icon="lucide:chevron-left" iconWidth={15} onClick={() => setCurrentPage(safePage - 1)} disabled={safePage <= 1}>
                  Précédent
                </AppButton>
                <span className="sw-pager__page">Page {safePage} / {pageCount}</span>
                <AppButton className="sw-btn sw-btn--page" onClick={() => setCurrentPage(safePage + 1)} disabled={safePage >= pageCount}>
                  Suivant
                  <Icon icon="lucide:chevron-right" width={15} />
                </AppButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default DomainListPage;
